import { CanvasProps } from '../types';
import { getGitHubIssueLink } from './links';

export const DEFAULT_LVGL_SCHEMA_VERSION: string = 'v9.4.0';

export const LVGL_INTERNAL_LICENSES: Record<string, string[]> = {
    development: ['68b996310023edb4a22a'],
    production: ['6900e44c0025558c4433']
};

export const XML_FILE_TYPE: DOMParserSupportedType = 'text/xml';
export const XML_LANGUAGE_ID: 'xml' = 'xml';
export const XML_FILE_EXTENSION: '.xml' = '.xml';
export const LVGL_LOG: 'lvgl-log' = 'lvgl-log';
export const COMPOUND_COORDINATE_TYPE: 'px|%|content' = 'px|%|content';
export const COMPOUND_COORDINATE_NO_CONTENT_TYPE: 'px|%' = 'px|%';
export const INTEGER_LIKE_TYPES = [
    'int',
    'px',
    '%',
    'percentage',
    'content',
    COMPOUND_COORDINATE_TYPE,
    COMPOUND_COORDINATE_NO_CONTENT_TYPE
] as const;
export type IntegerLikeType = (typeof INTEGER_LIKE_TYPES)[number];
const INTEGER_LIKE_TYPE_SET: ReadonlySet<string> = new Set<string>(INTEGER_LIKE_TYPES);
export const isIntegerLikeType = (type: string): type is IntegerLikeType => INTEGER_LIKE_TYPE_SET.has(type);
export const ONLINE_PREVIEW_LOGOUT: 'online-preview-logout' = 'online-preview-logout';
export const EDITOR_SHORTCUT: 'editor-shortcut' = 'editor-shortcut';

export const getReportIssueLink = (title: string, body: string): string => getGitHubIssueLink(title, body);

export const DEFAULT_RESPONSIVE_TARGET = {
    name: '',
    id: 0 // Start with the ID for the UNDEFINED target, so the Editor can always tell when the target has changed
};

export const DEFAULT_SCREEN_STYLE: Record<string, string> = {
    width: '100%',
    height: '100%',
    style_radius: '0'
};

export const DEFAULT_DISPLAY_PROPS: CanvasProps = {
    width: 320,
    height: 240,
    target: DEFAULT_RESPONSIVE_TARGET,
    zoom: 1,
    language: 'en',
    style: DEFAULT_SCREEN_STYLE
};

export const PROTECTED_ELEMENTS: ReadonlySet<string> = new Set<string>([
    'components',
    'component',
    'event_cbs',
    'event_cb',
    'fonts',
    'font',
    'globals',
    'global',
    'images',
    'image',
    'screens',
    'screen',
    'styles',
    'style',
    'subjects',
    'subject',
    'widgets',
    'widget'
]);

export const RESERVED_NAME_KEYWORDS: ReadonlySet<string> = new Set<string>([
    'alignas',
    'alignof',
    'and',
    'and_eq',
    'asm',
    'auto',
    'bool',
    'bitand',
    'bitor',
    'break',
    'case',
    'catch',
    'char',
    'char16_t',
    'char32_t',
    'class',
    'co_await',
    'co_return',
    'co_yield',
    'concept',
    'compl',
    'const',
    'const_cast',
    'constexpr',
    'continue',
    'decltype',
    'default',
    'delete',
    'do',
    'double',
    'dynamic_cast',
    'else',
    'enum',
    'explicit',
    'export',
    'extern',
    'false',
    'float',
    'for',
    'free',
    'friend',
    'goto',
    'if',
    'inline',
    'int',
    'long',
    'lv_',
    'lvgl',
    'main',
    'malloc',
    'mutable',
    'namespace',
    'new',
    'noexcept',
    'not',
    'not_eq',
    'nullptr',
    'operator',
    'or',
    'or_eq',
    'override',
    'private',
    'protected',
    'public',
    'register',
    'reinterpret_cast',
    'requires',
    'restrict',
    'return',
    'short',
    'signed',
    'sizeof',
    'static',
    'static_assert',
    'static_cast',
    'std',
    'struct',
    'template',
    'this',
    'thread_local',
    'throw',
    'true',
    'try',
    'typedef',
    'typeid',
    'typename',
    'union',
    'unsigned',
    'using',
    'virtual',
    'void',
    'volatile',
    'wchar_t',
    'while',
    'xor',
    'xor_eq',
    'widget',
    '_Alignas',
    '_Alignof',
    '_Atomic',
    '_Bool',
    '_Complex',
    '_Generic',
    '_Imaginary',
    '_Noreturn',
    '_Static_assert',
    '_Thread_local'
]);

export const RESERVED_PATH_KEYWORDS: ReadonlySet<string> = new Set<string>([
    'api',
    'bin',
    'component',
    'consts',
    'data',
    'error',
    'file',
    'float',
    'fonts',
    'images',
    'int',
    'screen',
    'string',
    'style',
    'styles',
    'subjects',
    'tiny_ttf',
    'view',
    'widget'
]);

export const UNIQUE_ELEMENTS: Record<string, boolean> = {
    api: false,
    component: false,
    previews: false,
    screen: false,
    subjects: false
};

export const VALID_C_VAR_NAME: RegExp = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export const VALID_C_VAR_NAME_DESCRIPTION: string =
    'Must start with a letter or underscore, and contain only letters, numbers, or underscores.';

export const INVALID_C_IDENTIFIER_MESSAGE: string = 'Use a valid C identifier.';

export const VALID_PROJECT_NAME_REGEX: RegExp = VALID_C_VAR_NAME;

export const VALID_PROJECT_NAME_DESCRIPTION: string = VALID_C_VAR_NAME_DESCRIPTION;

// Something like: target|low_mem|target2|custom_target etc.
export const VALID_IF_TARGET_REGEX: RegExp = /^[a-zA-Z_][a-zA-Z0-9_]*(\|[a-zA-Z_][a-zA-Z0-9_]*)*$/;

export const VALID_IF_TARGET_DESCRIPTION: string =
    'Target names must start with a letter or underscore, and contain only letters, numbers, or underscores. Targets may be OR-ed with |, e.g. "target1|target2".';
